import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { broadcastApi } from '../api';
import api from '../api/axios';
import toast from 'react-hot-toast';
import {
    FiPlus, FiEdit2, FiTrash2, FiBarChart2, FiToggleLeft, FiToggleRight,
    FiUpload, FiX, FiUsers, FiEye, FiCheckCircle, FiMousePointer
} from 'react-icons/fi';
import RichTextEditor from '../components/Broadcast/RichTextEditor';
import {
    inputClass, btnPrimary, btnGhost, iconBtn, th, statusPill,
    toLocalInput, scheduleState, nowLocalInput, addDaysLocalInput,
    toIsoInstant, expiryFor, formatEnds, EXPIRY_OPTIONS
} from '../components/Broadcast/ui';

// Built per open, so "Starts" defaults to the current minute rather than
// whatever the clock said when the page first loaded.
function blankForm() {
    return {
        embed_id: '',
        heading: '',
        message: '',
        image_url: '',
        button_label: '',
        button_url: '',
        audience: 'all',
        page_mode: 'all',
        page_paths: '',
        frequency: 'once',
        starts_at: nowLocalInput(),
        expiry: '',
        ends_at: '',
        is_active: true
    };
}

const AUDIENCE_LABEL = { all: 'Everyone', guests: 'Guests only', members: 'Signed-in only' };
const FREQUENCY_LABEL = {
    once: 'Once per visitor',
    session: 'Once per visit',
    always: 'Every page load'
};
const PAGE_LABEL = { all: 'Every page', include: 'Only these pages', exclude: 'Everywhere except' };

function pagesSummary(row) {
    if (row.page_mode === 'all') return 'Every page';
    const paths = row.page_paths || [];
    if (!paths.length) return 'Every page';
    const prefix = row.page_mode === 'include' ? '' : 'except ';
    return prefix + paths.join(', ');
}

function pct(part, whole) {
    if (!whole) return '—';
    return Math.round((part / whole) * 100) + '%';
}

export default function BroadcastsPage() {
    const [broadcasts, setBroadcasts] = useState([]);
    const [panels, setPanels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(blankForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [statsFor, setStatsFor] = useState(null);
    const [stats, setStats] = useState(null);

    const fetchAll = async () => {
        try {
            const [listRes, panelRes] = await Promise.all([
                broadcastApi.list(),
                broadcastApi.listPanels()
            ]);
            setBroadcasts(listRes.data.broadcasts || []);
            setPanels(panelRes.data.embeds || []);
        } catch (err) {
            toast.error('Failed to load broadcasts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const openAdd = () => {
        setEditing(null);
        setForm(blankForm());
        setShowModal(true);
    };

    const openEdit = (row) => {
        const startsAt = toLocalInput(row.starts_at) || nowLocalInput();
        const expiry = expiryFor(startsAt, row.ends_at);
        setEditing(row);
        setForm({
            embed_id: row.embed_id ? String(row.embed_id) : '',
            heading: row.heading || '',
            message: row.message || '',
            image_url: row.image_url || '',
            button_label: row.button_label || '',
            button_url: row.button_url || '',
            audience: row.audience || 'all',
            page_mode: row.page_mode || 'all',
            page_paths: (row.page_paths || []).join(', '),
            frequency: row.frequency || 'once',
            starts_at: startsAt,
            expiry: expiry.expiry,
            ends_at: expiry.ends_at,
            is_active: !!row.is_active
        });
        setShowModal(true);
    };

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (data.success && data.data?.url) {
                setForm(prev => ({ ...prev, image_url: data.data.url }));
                toast.success('Image uploaded');
            } else {
                toast.error('Upload failed');
            }
        } catch (err) {
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!form.heading.trim()) return toast.error('Heading is required');
        setSaving(true);
        try {
            const startsAt = form.starts_at || null;
            // The end time comes from the expiry selector; 'custom' carries over a
            // window an older row already had, so editing another field can't move it.
            let endsAt = null;
            if (form.expiry === 'custom') {
                endsAt = form.ends_at || null;
            } else if (form.expiry) {
                endsAt = addDaysLocalInput(startsAt || nowLocalInput(), parseInt(form.expiry, 10)) || null;
            }

            const payload = {
                embed_id: form.embed_id ? parseInt(form.embed_id, 10) : null,
                heading: form.heading,
                message: form.message,
                image_url: form.image_url,
                button_label: form.button_label,
                button_url: form.button_url,
                audience: form.audience,
                page_mode: form.page_mode,
                page_paths: form.page_paths.split(/[\s,]+/).filter(Boolean),
                frequency: form.frequency,
                starts_at: toIsoInstant(startsAt),
                ends_at: toIsoInstant(endsAt),
                // The checkbox only exists while editing; a new broadcast is live.
                is_active: editing ? form.is_active : true
            };
            if (editing) {
                await broadcastApi.update(editing.id, payload);
                toast.success('Broadcast updated');
            } else {
                await broadcastApi.create(payload);
                toast.success('Broadcast created');
            }
            setShowModal(false);
            fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save broadcast');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (row) => {
        try {
            await broadcastApi.update(row.id, { ...row, is_active: !row.is_active });
            toast.success(row.is_active ? 'Broadcast switched off' : 'Broadcast switched on');
            fetchAll();
        } catch (err) {
            toast.error('Failed to update broadcast');
        }
    };

    const handleDelete = async (row) => {
        if (!confirm(`Delete "${row.heading}" and its statistics? This cannot be undone.`)) return;
        try {
            await broadcastApi.delete(row.id);
            toast.success('Broadcast deleted');
            fetchAll();
        } catch (err) {
            toast.error('Failed to delete broadcast');
        }
    };

    const openStats = async (row) => {
        setStatsFor(row);
        setStats(null);
        try {
            const { data } = await broadcastApi.stats(row.id);
            setStats(data);
        } catch (err) {
            toast.error('Failed to load statistics');
            setStatsFor(null);
        }
    };

    const panelName = (id) => {
        if (!id) return 'All panels';
        const match = panels.find(p => p.id === id);
        return match ? match.name : `#${id}`;
    };

    return (
        <DashboardLayout>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Broadcasts</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Popups shown to the users of your broadcast panels.
                    </p>
                </div>
                <button onClick={openAdd} disabled={!panels.length} className={`${btnPrimary} w-full sm:w-auto`} title={panels.length ? '' : 'Add a broadcast panel first'}>
                    <FiPlus size={16} /> New Broadcast
                </button>
            </div>

            {!loading && !panels.length && (
                <div className="mb-6 rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
                    Add a panel under <strong className="text-foreground">Broadcast Panels</strong> first — a broadcast needs
                    somewhere to appear.
                </div>
            )}

            <div className="card overflow-hidden">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <div className="overflow-x-auto min-h-[200px]">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead>
                                <tr className="border-b border-border bg-muted/20">
                                    <th className={th}>Heading</th>
                                    <th className={`${th} hidden md:table-cell`}>Target</th>
                                    <th className={`${th} hidden lg:table-cell`}>Audience</th>
                                    <th className={`${th} hidden lg:table-cell`}>Pages</th>
                                    <th className={`${th} hidden xl:table-cell`}>Repeats</th>
                                    <th className={th}>Status</th>
                                    <th className={`${th} text-right`}>Seen</th>
                                    <th className={`${th} text-right hidden sm:table-cell`}>Closed</th>
                                    <th className={`${th} text-right hidden sm:table-cell`}>Clicks</th>
                                    <th className={`${th} text-right`}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                                {broadcasts.map(row => {
                                    const state = scheduleState(row);
                                    return (
                                        <tr key={row.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                                            <td className="p-4 align-middle max-w-[280px]">
                                                <div className="font-medium text-foreground truncate">{row.heading}</div>
                                                <div className="text-xs text-muted-foreground truncate md:hidden">
                                                    {panelName(row.embed_id)} · {AUDIENCE_LABEL[row.audience]}
                                                </div>
                                            </td>
                                            <td className={`p-4 align-middle hidden md:table-cell text-muted-foreground`}>
                                                {panelName(row.embed_id)}
                                            </td>
                                            <td className={`p-4 align-middle hidden lg:table-cell text-muted-foreground`}>
                                                {AUDIENCE_LABEL[row.audience]}
                                            </td>
                                            <td className={`p-4 align-middle hidden lg:table-cell text-muted-foreground max-w-[180px] truncate`} title={pagesSummary(row)}>
                                                {pagesSummary(row)}
                                            </td>
                                            <td className={`p-4 align-middle hidden xl:table-cell text-muted-foreground`}>
                                                {FREQUENCY_LABEL[row.frequency]}
                                            </td>
                                            <td className="p-4 align-middle">
                                                <span className={statusPill(state.tone)}>{state.label}</span>
                                            </td>
                                            <td className="p-4 align-middle text-right font-medium text-foreground">{row.impressions}</td>
                                            <td className={`p-4 align-middle text-right hidden sm:table-cell text-muted-foreground`}>{row.dismissals}</td>
                                            <td className={`p-4 align-middle text-right hidden sm:table-cell text-muted-foreground`}>{row.actions}</td>
                                            <td className="p-4 align-middle">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => openStats(row)} className={iconBtn} title="Statistics">
                                                        <FiBarChart2 size={15} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggle(row)}
                                                        className={iconBtn}
                                                        title={row.is_active ? 'Switch off' : 'Switch on'}
                                                    >
                                                        {row.is_active ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                                                    </button>
                                                    <button onClick={() => openEdit(row)} className={iconBtn} title="Edit">
                                                        <FiEdit2 size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(row)}
                                                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {broadcasts.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="p-8 text-center text-muted-foreground">
                                            No broadcasts yet. Click "New Broadcast" to write one.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                    <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                            <h3 className="text-lg font-semibold tracking-tight text-foreground">
                                {editing ? 'Edit Broadcast' : 'New Broadcast'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className={iconBtn} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-6 space-y-5">
                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Heading *</label>
                                <input type="text" required maxLength={160} value={form.heading}
                                    onChange={e => setForm({ ...form, heading: e.target.value })}
                                    className={inputClass} placeholder="e.g. Scheduled maintenance tonight" />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Message *</label>
                                <RichTextEditor value={form.message} onChange={html => setForm(prev => ({ ...prev, message: html }))} />
                                <p className="text-xs text-muted-foreground">
                                    Bold, italic, underline, links and bullet lists are kept. Anything else is stripped
                                    before it reaches the panel.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium leading-none text-foreground">Image</label>
                                <div className="flex gap-2">
                                    <input type="url" value={form.image_url}
                                        onChange={e => setForm({ ...form, image_url: e.target.value })}
                                        className={inputClass + ' flex-1'} placeholder="https://…" />
                                    <label className={`${btnGhost} cursor-pointer`}>
                                        <FiUpload size={15} /> {uploading ? '…' : 'Upload'}
                                        <input type="file" accept="image/*" className="hidden"
                                            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
                                    </label>
                                </div>
                                {form.image_url && (
                                    <img src={form.image_url} alt="" className="mt-1 max-h-24 rounded border border-border object-contain"
                                        onError={e => { e.target.style.display = 'none'; }} />
                                )}
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Button label</label>
                                    <input type="text" maxLength={60} value={form.button_label}
                                        onChange={e => setForm({ ...form, button_label: e.target.value })}
                                        className={inputClass} placeholder="e.g. Read more" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Button link</label>
                                    <input type="url" value={form.button_url}
                                        onChange={e => setForm({ ...form, button_url: e.target.value })}
                                        className={inputClass} placeholder="https://…" />
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Show on panel</label>
                                    <select value={form.embed_id}
                                        onChange={e => setForm({ ...form, embed_id: e.target.value })}
                                        className={inputClass}>
                                        <option value="">All panels</option>
                                        {panels.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Who sees it</label>
                                    <select value={form.audience}
                                        onChange={e => setForm({ ...form, audience: e.target.value })}
                                        className={inputClass}>
                                        <option value="all">Everyone</option>
                                        <option value="guests">Guests only</option>
                                        <option value="members">Signed-in users only</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Which pages</label>
                                    <select value={form.page_mode}
                                        onChange={e => setForm({ ...form, page_mode: e.target.value })}
                                        className={inputClass}>
                                        <option value="all">Every page</option>
                                        <option value="include">Only these pages</option>
                                        <option value="exclude">Everywhere except these</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Page paths</label>
                                    <input type="text" value={form.page_paths}
                                        onChange={e => setForm({ ...form, page_paths: e.target.value })}
                                        disabled={form.page_mode === 'all'}
                                        className={inputClass} placeholder="/orders, /services/*" />
                                </div>
                            </div>
                            <p className="-mt-3 text-xs text-muted-foreground">
                                <code>/orders</code> matches one page, <code>/orders/*</code> matches everything under it.
                            </p>

                            <div className="grid gap-4 sm:grid-cols-3">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Repeats</label>
                                    <select value={form.frequency}
                                        onChange={e => setForm({ ...form, frequency: e.target.value })}
                                        className={inputClass}>
                                        <option value="once">Once per visitor</option>
                                        <option value="session">Once per visit</option>
                                        <option value="always">Every page load</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Starts</label>
                                    <input type="datetime-local" value={form.starts_at}
                                        onChange={e => setForm({ ...form, starts_at: e.target.value })}
                                        className={inputClass} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium leading-none text-foreground">Expires</label>
                                    <select value={form.expiry}
                                        onChange={e => setForm({ ...form, expiry: e.target.value })}
                                        className={inputClass}>
                                        {EXPIRY_OPTIONS.map(option => (
                                            <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                                        ))}
                                        {form.expiry === 'custom' && (
                                            <option value="custom">Keep current ({formatEnds(form.ends_at)})</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Create always goes live; the toggle belongs to editing an
                                existing broadcast, where switching one off is the point. */}
                            {editing && (
                                <label className="flex items-center gap-2 text-sm text-foreground">
                                    <input type="checkbox" checked={form.is_active}
                                        onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                                    Active
                                </label>
                            )}

                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" onClick={() => setShowModal(false)} className={btnGhost}>Cancel</button>
                                <button type="submit" disabled={saving} className={btnPrimary}>
                                    {saving ? 'Saving…' : editing ? 'Update' : 'Create Broadcast'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {statsFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
                    <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                            <h3 className="text-lg font-semibold tracking-tight text-foreground truncate">{statsFor.heading}</h3>
                            <button onClick={() => { setStatsFor(null); setStats(null); }} className={iconBtn} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            {!stats ? (
                                <div className="flex justify-center items-center h-32">
                                    <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                        <div className="rounded-lg border border-border p-3">
                                            <FiEye size={15} className="text-muted-foreground" />
                                            <div className="mt-1 text-xl font-semibold text-foreground">{stats.totals.impression}</div>
                                            <div className="text-xs text-muted-foreground">Seen</div>
                                        </div>
                                        <div className="rounded-lg border border-border p-3">
                                            <FiCheckCircle size={15} className="text-muted-foreground" />
                                            <div className="mt-1 text-xl font-semibold text-foreground">{stats.totals.dismiss}</div>
                                            <div className="text-xs text-muted-foreground">Closed</div>
                                        </div>
                                        <div className="rounded-lg border border-border p-3">
                                            <FiMousePointer size={15} className="text-muted-foreground" />
                                            <div className="mt-1 text-xl font-semibold text-foreground">{stats.totals.action}</div>
                                            <div className="text-xs text-muted-foreground">Button clicks</div>
                                        </div>
                                        <div className="rounded-lg border border-border p-3">
                                            <FiUsers size={15} className="text-muted-foreground" />
                                            <div className="mt-1 text-xl font-semibold text-foreground">
                                                {pct(stats.totals.action, stats.totals.impression)}
                                            </div>
                                            <div className="text-xs text-muted-foreground">Click rate</div>
                                        </div>
                                    </div>

                                    {stats.by_embed?.length ? (
                                        <div className="overflow-x-auto rounded-lg border border-border">
                                            <table className="w-full text-left text-sm">
                                                <thead>
                                                    <tr className="border-b border-border bg-muted/20">
                                                        <th className={th}>Panel</th>
                                                        <th className={`${th} text-right`}>Seen</th>
                                                        <th className={`${th} text-right`}>Closed</th>
                                                        <th className={`${th} text-right`}>Clicks</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="[&_tr:last-child]:border-0">
                                                    {Object.values(
                                                        stats.by_embed.reduce((acc, row) => {
                                                            const key = row.embed_id;
                                                            if (!acc[key]) acc[key] = { name: row.embed_name, impression: 0, dismiss: 0, action: 0 };
                                                            acc[key][row.event] = row.total;
                                                            return acc;
                                                        }, {})
                                                    ).map((row, i) => (
                                                        <tr key={i} className="border-b border-border">
                                                            <td className="p-3 align-middle text-foreground">{row.name}</td>
                                                            <td className="p-3 align-middle text-right">{row.impression || 0}</td>
                                                            <td className="p-3 align-middle text-right">{row.dismiss || 0}</td>
                                                            <td className="p-3 align-middle text-right">{row.action || 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            Nobody has seen this broadcast yet.
                                        </p>
                                    )}

                                    <p className="text-xs text-muted-foreground">
                                        Each number counts unique visitors, so one person refreshing a page is counted once.
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}
